import React, { useState, useEffect, useRef } from "react";
import { supabase } from "./supabase";
import StandardModal from "./StandardModal";
import {
  BarChart3,
  Plus,
  Edit,
  Trash2,
  Save,
  X,
  Search,
  Filter,
  Download,
  Upload,
  Calendar,
  Clock,
  Eye,
  AlertCircle,
  CheckCircle2,
  XCircle,
  Info,
  ChevronDown,
  DollarSign,
  FileText,
  User,
  Users,
  Building2,
  MapPin,
  Loader2,
  CheckSquare,
  Square,
  Star,
  Target,
  Package,
} from "lucide-react";

// Configuración anti-zoom móvil
if (typeof document !== "undefined") {
  let metaViewport = document.querySelector('meta[name="viewport"]');
  if (!metaViewport) {
    metaViewport = document.createElement("meta");
    metaViewport.name = "viewport";
    document.head.appendChild(metaViewport);
  }
  metaViewport.content =
    "width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no, viewport-fit=cover";
  
  // Agregar estilos CSS para animaciones
  if (!document.getElementById('notas-proyecto-styles')) {
    const style = document.createElement('style');
    style.id = 'notas-proyecto-styles';
    style.textContent = `
      @keyframes slideDown {
        from {
          opacity: 0;
          transform: translateY(-10px);
        }
        to {
          opacity: 1;
          transform: translateY(0);
        }
      }
    `;
    document.head.appendChild(style);
  }
}

// Función para obtener colores de vista
const getVistaColor = () => ({
  gradient: "linear-gradient(135deg, #2563eb 0%, #2563eb 100%)",
  solid: "#2563eb",
  light: "#2563eb10",
  description: "Notas y tareas del proyecto",
});

// Función para formatear fecha
const formatDateForDisplay = (dateString) => {
  if (!dateString) return "";
  const [year, month, day] = dateString.split("T")[0].split("-");
  return `${day}/${month}/${year}`;
};

// Dropdown personalizado unificado con búsqueda
const CustomDropdown = ({
  value,
  onChange,
  options,
  placeholder,
  disabled = false,
  icon: Icon,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedOption, setSelectedOption] = useState(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const selected = options.find((opt) => opt.value === value);
    setSelectedOption(selected);
  }, [value, options]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div ref={dropdownRef} style={{ position: "relative", width: "100%" }}>
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        style={{
          width: "100%",
          padding: "12px 16px",
          border: "2px solid #f1f5f9",
          borderRadius: "12px",
          background: disabled ? "#f8fafc" : "white",
          color: disabled ? "#94a3b8" : "#1e293b",
          fontSize: "16px",
          fontWeight: "500",
          textAlign: "left",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          cursor: disabled ? "not-allowed" : "pointer",
          outline: "none",
          transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
          minHeight: "44px",
          touchAction: "manipulation",
        }}
      >
        <div
          style={{ display: "flex", alignItems: "center", gap: "8px", flex: 1 }}
        >
          {Icon && <Icon size={16} color="#64748b" />}
          <span
            style={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {selectedOption ? selectedOption.label : placeholder}
          </span>
        </div>
        <ChevronDown
          size={16}
          style={{
            transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
            flexShrink: 0,
            color: "#64748b",
          }}
        />
      </button>

      {isOpen && (
        <div
          style={{
            position: "absolute",
            top: "100%",
            left: 0,
            right: 0,
            zIndex: 1000,
            background: "white",
            border: "1px solid #e2e8f0",
            borderRadius: "12px",
            boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.1)",
            marginTop: "4px",
            maxHeight: "280px",
            overflowY: "auto",
          }}
        >
          {options.map((option) => (
            <button
              key={option.value}
              onClick={() => {
                onChange(option.value);
                setIsOpen(false);
              }}
              style={{
                width: "100%",
                padding: "12px 16px",
                border: "none",
                background: option.value === value ? "#eff6ff" : "transparent",
                color: option.value === value ? "#3b82f6" : "#374151",
                fontSize: "16px",
                fontWeight: option.value === value ? "600" : "400",
                textAlign: "left",
                cursor: "pointer",
                transition: "all 0.15s cubic-bezier(0.4, 0, 0.2, 1)",
                touchAction: "manipulation",
              }}
            >
              {option.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

function NotasProyecto({ constructora, usuario, permisosCliente }) {
  const [notas, setNotas] = useState([]);
  const [proyectos, setProyectos] = useState([]);
  const [trabajadores, setTrabajadores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingNota, setEditingNota] = useState(null);
  const [guardando, setGuardando] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [filtros, setFiltros] = useState({
    proyecto: "",
    prioridad: "",
    estado: "",
    trabajador: "",
  });
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window !== "undefined") {
      return window.innerWidth <= 768;
    }
    return false;
  });

  // Estados para filtros
  const [proyectosFiltro, setProyectosFiltro] = useState([]);
  const [trabajadoresFiltro, setTrabajadoresFiltro] = useState([]);

  // Estado para modal personalizado
  const [customModal, setCustomModal] = useState({
    isOpen: false,
    type: "info",
    title: "",
    message: "",
    onConfirm: null,
  });

  // Cargar datos iniciales
  useEffect(() => {
    setIsMobile(window.innerWidth <= 768);

    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    cargarDatos();
  }, [constructora?.id]);

  const cargarDatos = async () => {
    if (!constructora?.id) {
      return;
    }
    
    setLoading(true);
    try {
      
      // Determinar permisos
      const isCliente = usuario?.tipo_usuario === "cliente";
      
      // Cargar proyectos activos
      let query = supabase
        .from("proyectos")
        .select("*")
        .eq("constructora_id", constructora.id)
        .eq("activo", true)
        .is("deleted_at", null);

      // Si es cliente, limitar a su proyecto
      if (isCliente && permisosCliente?.proyectoId) {
        query = query.eq("id", permisosCliente.proyectoId);
      }

      const { data: proyectosData, error: proyectosError } = await query.order("codigo");

      if (proyectosError) {
        console.error("Error cargando proyectos:", proyectosError);
      } else {
        setProyectos(proyectosData || []);
        setProyectosFiltro(proyectosData || []);
      }

      // Cargar trabajadores
      const { data: trabajadoresData, error: trabajadoresError } = await supabase
        .from("trabajadores")
        .select("id, nombre, apellido, tipo")
        .eq("constructora_id", constructora.id)
        .eq("activo", true)
        .order("nombre");

      if (trabajadoresError) {
        console.error("Error cargando trabajadores:", trabajadoresError);
      } else {
        setTrabajadores(trabajadoresData || []);
        setTrabajadoresFiltro(trabajadoresData || []);
      }

      // Cargar notas
      await cargarNotas();
    } catch (error) {
      console.error("Error cargando datos:", error);
    } finally {
      setLoading(false);
    }
  };

  const cargarNotas = async () => {
    try {
      
      let query = supabase
        .from("notas_proyecto")
        .select(`
          *,
          proyectos(codigo, apellido_cliente),
          trabajadores(nombre, apellido, tipo)
        `)
        .eq("constructora_id", constructora.id)
        .eq("activo", true)
        .order("created_at", { ascending: false });

      // Aplicar filtros
      if (filtros.proyecto) {
        query = query.eq("proyecto_id", filtros.proyecto);
      }
      if (filtros.prioridad) {
        query = query.eq("prioridad", filtros.prioridad);
      }
      if (filtros.estado) {
        query = query.eq("estado", filtros.estado);
      }
      if (filtros.trabajador) {
        query = query.eq("trabajador_id", filtros.trabajador);
      }

      const { data, error } = await query;

      if (error) {
        console.error("Error cargando notas:", error);
        return;
      }

      setNotas(data || []);
    } catch (error) {
      console.error("Error cargando notas:", error);
    }
  };

  // Aplicar filtros
  useEffect(() => {
    cargarNotas();
  }, [filtros]);

  // Bloquear scroll del body cuando el modal está abierto
  useEffect(() => {
    if (showModal) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    // Limpiar al desmontar
    return () => {
      document.body.style.overflow = "";
    };
  }, [showModal]);

  const aplicarFiltros = () => {
    cargarNotas();
  };

  const limpiarFiltros = () => {
    setFiltros({
      proyecto: "",
      prioridad: "",
      estado: "",
      trabajador: "",
    });
  };



  const handleSave = async () => {
    if (!editingNota.titulo) {
      showCustomAlert("error", "Error", "El título es obligatorio");
      return;
    }

    setGuardando(true);
    try {
      const dataToSave = {
        constructora_id: constructora.id,
        proyecto_id: editingNota.proyecto_id || null,
        titulo: editingNota.titulo.trim(),
        descripcion: editingNota.descripcion?.trim() || null,
        prioridad: editingNota.prioridad,
        estado: editingNota.estado,
        trabajador_id: editingNota.trabajador_id || null,
      };

      if (editingNota.id) {
        // Actualización
        dataToSave.updated_by = usuario.id;
        const { error } = await supabase
          .from("notas_proyecto")
          .update(dataToSave)
          .eq("id", editingNota.id);

        if (error) throw error;
        showCustomAlert("success", "Éxito", "Nota actualizada correctamente");
      } else {
        // Creación
        dataToSave.created_by = usuario.id;
        dataToSave.updated_by = usuario.id;
        const { error } = await supabase
          .from("notas_proyecto")
          .insert([dataToSave]);

        if (error) throw error;
        showCustomAlert("success", "Éxito", "Nota creada correctamente");
      }

      setShowModal(false);
      setEditingNota(null);
      
      // Forzar recarga inmediata
      setTimeout(async () => {
        await cargarNotas();
      }, 100);
    } catch (error) {
      console.error("Error guardando nota:", error);
      showCustomAlert("error", "Error", "Error al guardar: " + error.message);
    } finally {
      setGuardando(false);
    }
  };

  const handleDelete = async (nota) => {
    showCustomAlert(
      "confirm",
      "¿Eliminar nota?",
      `¿Estás seguro de que quieres eliminar la nota "${nota.titulo}"?`,
      async () => {
        try {
          const { error } = await supabase
            .from("notas_proyecto")
            .update({
              activo: false,
              deleted_at: new Date().toISOString(),
              updated_by: usuario.id,
            })
            .eq("id", nota.id);

          if (error) throw error;
          showCustomAlert("success", "Éxito", "Nota eliminada correctamente");
          cargarNotas();
        } catch (error) {
          console.error("Error eliminando nota:", error);
          showCustomAlert("error", "Error", "Error al eliminar: " + error.message);
        }
      }
    );
  };

  const handleRestore = async (nota) => {
    try {
      const { error } = await supabase
        .from("notas_proyecto")
        .update({
          activo: true,
          deleted_at: null,
          updated_by: usuario.id,
        })
        .eq("id", nota.id);

      if (error) throw error;
      showCustomAlert("success", "Éxito", "Nota restaurada correctamente");
      cargarNotas();
    } catch (error) {
      console.error("Error restaurando nota:", error);
      showCustomAlert("error", "Error", "Error al restaurar: " + error.message);
    }
  };

  const handleToggleEstado = async (nota) => {
    try {
      const nuevoEstado = nota.estado === "pendiente" ? "hecho" : "pendiente";
      const { error } = await supabase
        .from("notas_proyecto")
        .update({
          estado: nuevoEstado,
          updated_by: usuario.id,
        })
        .eq("id", nota.id);

      if (error) throw error;
      cargarNotas();
    } catch (error) {
      console.error("Error cambiando estado:", error);
      showCustomAlert("error", "Error", "Error al cambiar estado: " + error.message);
    }
  };

  const showCustomAlert = (type, title, message, onConfirm = null) => {
    setCustomModal({
      isOpen: true,
      type,
      title,
      message,
      onConfirm,
    });
  };

  const getPrioridadColor = (prioridad) => {
    switch (prioridad) {
      case "alta":
        return { bg: "#fef2f2", text: "#dc2626", border: "#fecaca" };
      case "media":
        return { bg: "#fffbeb", text: "#d97706", border: "#fed7aa" };
      case "baja":
        return { bg: "#f0fdf4", text: "#059669", border: "#bbf7d0" };
      default:
        return { bg: "#f8fafc", text: "#64748b", border: "#e2e8f0" };
    }
  };

  const getEstadoColor = (estado) => {
    switch (estado) {
      case "pendiente":
        return { bg: "#fef3c7", text: "#d97706", border: "#fde68a" };
      case "hecho":
        return { bg: "#d1fae5", text: "#059669", border: "#a7f3d0" };
      case "eliminado":
        return { bg: "#fee2e2", text: "#dc2626", border: "#fecaca" };
      default:
        return { bg: "#f8fafc", text: "#64748b", border: "#e2e8f0" };
    }
  };

  const getEstadoIcon = (estado) => {
    switch (estado) {
      case "pendiente":
        return <Square size={16} />;
      case "hecho":
        return <CheckSquare size={16} />;
      case "eliminado":
        return <XCircle size={16} />;
      default:
        return <Square size={16} />;
    }
  };

  const getPrioridadIcon = (prioridad) => {
    switch (prioridad) {
      case "alta":
        return <AlertCircle size={16} />;
      case "media":
        return <Target size={16} />;
      case "baja":
        return <Info size={16} />;
      default:
        return <Info size={16} />;
    }
  };

  // Preparar opciones para dropdowns
  const opcionesProyectos = proyectos.map((proyecto) => ({
    value: proyecto.id,
    label: `${proyecto.codigo} - ${proyecto.apellido_cliente}`,
  }));

  const notasFiltradas = notas.filter((nota) =>
    nota.titulo.toLowerCase().includes(searchTerm.toLowerCase()) ||
    nota.descripcion?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    nota.proyectos?.codigo?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    nota.proyectos?.apellido_cliente?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const colorVista = getVistaColor();

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          background: "#f8fafc",
        }}
      >
        <Loader2 size={48} color="#2563eb" style={{ animation: "spin 1s linear infinite" }} />
      </div>
    );
  }

  return (
    <div
      className="main-container"
      style={{
        padding: isMobile ? "16px 12px 120px 12px" : "20px",
        maxWidth: "100%",
        margin: "0 auto",
        background: "#f8fafc",
        minHeight: "100vh",
        animation: "fadeInUp 0.5s cubic-bezier(0.4, 0, 0.2, 1)",
      }}
    >
      {/* Header UNIFICADO */}
      <div
        className="header-section"
        style={{
          marginBottom: "20px",
          animation: "slideInRight 0.6s cubic-bezier(0.4, 0, 0.2, 1)",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: "20px",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <div
              style={{
                width: "40px",
                height: "40px",
                background: "linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)",
                borderRadius: "12px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 4px 16px #2563eb10",
              }}
            >
              <FileText size={20} color="white" />
            </div>
            <div>
              <h1
                style={{
                  fontSize: isMobile ? "20px" : "24px",
                  fontWeight: "700",
                  color: "#0f172a",
                  margin: 0,
                }}
              >
                Notas del Proyecto
              </h1>
              <p
                style={{
                  fontSize: "14px",
                  color: "#64748b",
                  margin: "4px 0 0 0",
                }}
              >
                {colorVista.description}
              </p>
            </div>
          </div>

          <button
            onClick={() => {
              setEditingNota({
                titulo: "",
                descripcion: "",
                proyecto_id: "",
                prioridad: "media",
                estado: "pendiente",
                trabajador_id: "",
              });
              setShowModal(true);
            }}
            style={{
              background: "linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)",
              color: "white",
              border: "none",
              borderRadius: "12px",
              padding: "12px 20px",
              fontSize: "16px",
              fontWeight: "600",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "8px",
              transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
              boxShadow: "0 4px 16px #2563eb20",
            }}
          >
            <Plus size={20} />
            Nueva Nota
          </button>
        </div>

        {/* Filtros */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '16px',
          marginBottom: '20px'
        }}>
          {/* Búsqueda y botón de filtros */}
          <div style={{ 
            display: 'flex', 
            gap: '12px', 
            alignItems: 'center',
            gridColumn: isMobile ? '1' : '1 / -1' 
          }}>
            <div style={{ position: 'relative', flex: 1 }}>
              <Search size={16} style={{
                position: 'absolute',
                left: '12px',
                top: '50%',
                transform: 'translateY(-50%)',
                color: '#64748b',
                zIndex: 1
              }} />
              <input
                type="text"
                placeholder="Buscar en títulos, descripciones, proyectos..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                autoFocus={false}
                autoComplete="off"
                style={{
                  width: '100%',
                  padding: '14px 16px 14px 44px',
                  border: '2px solid #f1f5f9',
                  borderRadius: '12px',
                  fontSize: '16px',
                  WebkitAppearance: 'none',
                  appearance: 'none',
                  background: 'white',
                  boxShadow: '0 4px 16px rgba(0,0,0,0.04)',
                  outline: 'none',
                  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                  boxSizing: 'border-box',
                  touchAction: 'manipulation'
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#3b82f6';
                  e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = '#f1f5f9';
                  e.target.style.boxShadow = '0 4px 16px rgba(0,0,0,0.04)';
                }}
              />
            </div>
            
            {/* Botón para mostrar/ocultar filtros */}
            <button
              onClick={() => setShowFilters(!showFilters)}
              style={{
                background: showFilters ? colorVista.solid : 'white',
                color: showFilters ? 'white' : '#64748b',
                border: '2px solid #f1f5f9',
                borderRadius: '12px',
                padding: '14px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                minWidth: '48px',
                minHeight: '48px'
              }}
              onMouseEnter={(e) => {
                if (!showFilters) {
                  e.target.style.borderColor = colorVista.solid;
                  e.target.style.color = colorVista.solid;
                }
              }}
              onMouseLeave={(e) => {
                if (!showFilters) {
                  e.target.style.borderColor = '#f1f5f9';
                  e.target.style.color = '#64748b';
                }
              }}
            >
              <Filter size={16} />
            </button>
          </div>

          {/* Filtros colapsables */}
          {showFilters && (
            <div style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: '12px',
              gridColumn: isMobile ? '1' : '1 / -1',
              animation: 'slideDown 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
            }}>
              {/* Filtro Proyecto */}
              <div>
                <label
                  style={{
                    fontSize: "12px",
                    color: "#2563eb",
                    fontWeight: "600",
                    display: "block",
                    marginBottom: "6px",
                  }}
                >
                  Proyecto
                </label>
                <CustomDropdown
                  value={filtros.proyecto}
                  onChange={(value) => setFiltros({ ...filtros, proyecto: value })}
                  options={[
                    { value: "", label: "Todos los proyectos" },
                    ...opcionesProyectos,
                  ]}
                  placeholder="Seleccionar proyecto"
                  icon={Building2}
                />
              </div>

              {/* Filtro Prioridad */}
              <div>
                <label
                  style={{
                    fontSize: "12px",
                    color: "#2563eb",
                    fontWeight: "600",
                    display: "block",
                    marginBottom: "6px",
                  }}
                >
                  Prioridad
                </label>
                <CustomDropdown
                  value={filtros.prioridad}
                  onChange={(value) => setFiltros({ ...filtros, prioridad: value })}
                  options={[
                    { value: "", label: "Todas las prioridades" },
                    { value: "alta", label: "Alta" },
                    { value: "media", label: "Media" },
                    { value: "baja", label: "Baja" },
                  ]}
                  placeholder="Seleccionar prioridad"
                  icon={Star}
                />
              </div>

              {/* Filtro Estado */}
              <div>
                <label
                  style={{
                    fontSize: "12px",
                    color: "#2563eb",
                    fontWeight: "600",
                    display: "block",
                    marginBottom: "6px",
                  }}
                >
                  Estado
                </label>
                <CustomDropdown
                  value={filtros.estado}
                  onChange={(value) => setFiltros({ ...filtros, estado: value })}
                  options={[
                    { value: "", label: "Todos los estados" },
                    { value: "pendiente", label: "Pendiente" },
                    { value: "hecho", label: "Hecho" },
                    { value: "eliminado", label: "Eliminado" },
                  ]}
                  placeholder="Seleccionar estado"
                  icon={CheckSquare}
                />
              </div>

              {/* Filtro Trabajador */}
              <div>
                <label
                  style={{
                    fontSize: "12px",
                    color: "#2563eb",
                    fontWeight: "600",
                    display: "block",
                    marginBottom: "6px",
                  }}
                >
                  Trabajador
                </label>
                <CustomDropdown
                  value={filtros.trabajador}
                  onChange={(value) => setFiltros({ ...filtros, trabajador: value })}
                  options={[
                    { value: "", label: "Todos los trabajadores" },
                    ...trabajadoresFiltro.map((t) => ({
                      value: t.id,
                      label: `${t.nombre} ${t.apellido} (${t.tipo})`,
                    })),
                  ]}
                  placeholder="Seleccionar trabajador"
                  icon={User}
                />
              </div>

              {/* Botones de filtros */}
              <div style={{ 
                display: "flex", 
                gap: "12px", 
                alignItems: "flex-end",
                gridColumn: isMobile ? '1' : '1 / -1'
              }}>
                <button
                  onClick={aplicarFiltros}
                  style={{
                    background: "linear-gradient(135deg, #2563eb 0%, #2563eb 100%)",
                    color: "white",
                    border: "none",
                    borderRadius: "8px",
                    padding: "8px 16px",
                    fontSize: "14px",
                    fontWeight: "600",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                  }}
                >
                  <Filter size={16} />
                  Aplicar Filtros
                </button>
                <button
                  onClick={limpiarFiltros}
                  style={{
                    background: "#f1f5f9",
                    color: "#64748b",
                    border: "none",
                    borderRadius: "8px",
                    padding: "8px 16px",
                    fontSize: "14px",
                    fontWeight: "600",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                  }}
                >
                  <X size={16} />
                  Limpiar
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Lista de notas */}
      <div
        style={{
          background: "white",
          borderRadius: "16px",
          padding: "24px",
          boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: "20px",
          }}
        >
          <h2
            style={{
              fontSize: "20px",
              fontWeight: "600",
              color: "#1e293b",
              margin: "0",
            }}
          >
            Notas ({notasFiltradas.length})
          </h2>
        </div>

        {notasFiltradas.length === 0 ? (
          <div
            style={{
              textAlign: "center",
              padding: "60px 20px",
              color: "#64748b",
            }}
          >
            <FileText size={48} style={{ marginBottom: "16px", opacity: 0.5 }} />
            <p style={{ fontSize: "16px", margin: "0" }}>
              {searchTerm || Object.values(filtros).some(f => f !== "")
                ? "No se encontraron notas con los filtros aplicados"
                : "No hay notas creadas aún"}
            </p>
            <p style={{ fontSize: "14px", margin: "8px 0 0 0", opacity: 0.7 }}>
              Crea tu primera nota para empezar a organizar tus tareas
            </p>
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: "0" }}>
            {notasFiltradas.map((nota) => {
              const prioridadColor = getPrioridadColor(nota.prioridad);
              const estadoColor = getEstadoColor(nota.estado);

              return (
                <div
                  key={nota.id}
                  style={{
                    border: "1px solid #e2e8f0",
                    borderRadius: "12px",
                    padding: "16px",
                    background: "white",
                    transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                    marginBottom: "12px",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "flex-start",
                      justifyContent: "space-between",
                      gap: "16px",
                    }}
                  >
                    {/* Contenido principal */}
                    <div style={{ flex: 1 }}>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "12px",
                          marginBottom: "8px",
                        }}
                      >
                        <button
                          onClick={() => handleToggleEstado(nota)}
                          style={{
                            background: "none",
                            border: "none",
                            cursor: "pointer",
                            padding: "4px",
                            borderRadius: "4px",
                            transition: "all 0.2s",
                          }}
                        >
                          {getEstadoIcon(nota.estado)}
                        </button>
                        <h3
                          style={{
                            fontSize: "16px",
                            fontWeight: "600",
                            color: "#1e293b",
                            margin: "0",
                            textDecoration: nota.estado === "hecho" ? "line-through" : "none",
                            opacity: nota.estado === "hecho" ? 0.6 : 1,
                          }}
                        >
                          {nota.titulo}
                        </h3>
                      </div>

                      {nota.descripcion && (
                        <p
                          style={{
                            fontSize: "14px",
                            color: "#64748b",
                            margin: "8px 0",
                            lineHeight: "1.5",
                          }}
                        >
                          {nota.descripcion}
                        </p>
                      )}

                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "12px",
                          marginTop: "12px",
                        }}
                      >
                        {/* Badge Proyecto */}
                        <div
                          style={{
                            background: "#f1f5f9",
                            color: "#64748b",
                            padding: "4px 8px",
                            borderRadius: "6px",
                            fontSize: "12px",
                            fontWeight: "500",
                            display: "flex",
                            alignItems: "center",
                            gap: "4px",
                          }}
                        >
                          <Building2 size={12} />
                          {nota.proyectos?.codigo} - {nota.proyectos?.apellido_cliente}
                        </div>

                        {/* Badge Prioridad */}
                        <div
                          style={{
                            background: prioridadColor.bg,
                            color: prioridadColor.text,
                            border: `1px solid ${prioridadColor.border}`,
                            padding: "4px 8px",
                            borderRadius: "6px",
                            fontSize: "12px",
                            fontWeight: "500",
                            display: "flex",
                            alignItems: "center",
                            gap: "4px",
                          }}
                        >
                          {getPrioridadIcon(nota.prioridad)}
                          {nota.prioridad.charAt(0).toUpperCase() + nota.prioridad.slice(1)}
                        </div>

                        {/* Badge Estado */}
                        <div
                          style={{
                            background: estadoColor.bg,
                            color: estadoColor.text,
                            border: `1px solid ${estadoColor.border}`,
                            padding: "4px 8px",
                            borderRadius: "6px",
                            fontSize: "12px",
                            fontWeight: "500",
                            display: "flex",
                            alignItems: "center",
                            gap: "4px",
                          }}
                        >
                          {getEstadoIcon(nota.estado)}
                          {nota.estado.charAt(0).toUpperCase() + nota.estado.slice(1)}
                        </div>

                        {/* Badge Trabajador */}
                        {nota.trabajadores && (
                          <div
                            style={{
                              background: "#f0f9ff",
                              color: "#0369a1",
                              border: "1px solid #bae6fd",
                              padding: "4px 8px",
                              borderRadius: "6px",
                              fontSize: "12px",
                              fontWeight: "500",
                              display: "flex",
                              alignItems: "center",
                              gap: "4px",
                            }}
                          >
                            <User size={12} />
                            {nota.trabajadores.nombre} {nota.trabajadores.apellido}
                          </div>
                        )}

                        {/* Fecha */}
                        <div
                          style={{
                            color: "#94a3b8",
                            fontSize: "12px",
                            display: "flex",
                            alignItems: "center",
                            gap: "4px",
                          }}
                        >
                          <Clock size={12} />
                          {formatDateForDisplay(nota.created_at)}
                        </div>
                      </div>
                    </div>

                    {/* Acciones */}
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                      }}
                    >
                      <button
                        onClick={() => {
                          setEditingNota(nota);
                          setShowModal(true);
                        }}
                        style={{
                          background: "#f1f5f9",
                          border: "none",
                          borderRadius: "6px",
                          padding: "6px",
                          cursor: "pointer",
                          transition: "all 0.2s",
                        }}
                      >
                        <Edit size={16} color="#64748b" />
                      </button>

                      {nota.activo ? (
                        <button
                          onClick={() => handleDelete(nota)}
                          style={{
                            background: "#fef2f2",
                            border: "none",
                            borderRadius: "6px",
                            padding: "6px",
                            cursor: "pointer",
                            transition: "all 0.2s",
                          }}
                        >
                          <Trash2 size={16} color="#dc2626" />
                        </button>
                      ) : (
                        <button
                          onClick={() => handleRestore(nota)}
                          style={{
                            background: "#f0fdf4",
                            border: "none",
                            borderRadius: "6px",
                            padding: "6px",
                            cursor: "pointer",
                            transition: "all 0.2s",
                          }}
                        >
                          <CheckCircle2 size={16} color="#059669" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Modal para crear/editar nota */}
      {showModal && (
        <StandardModal
          isOpen={showModal}
          onClose={() => {
            setShowModal(false);
            setEditingNota(null);
          }}
          title={editingNota?.id ? "Editar Nota" : "Nueva Nota"}
          size="lg"
        >
          <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
            {/* Título */}
            <div>
                          <label
              style={{
                fontSize: "14px",
                color: "#2563eb",
                fontWeight: "600",
                display: "block",
                marginBottom: "6px",
              }}
            >
              Título *
            </label>
              <input
                type="text"
                value={editingNota?.titulo || ""}
                onChange={(e) =>
                  setEditingNota({ ...editingNota, titulo: e.target.value })
                }
                placeholder="Título de la nota"
                style={{
                  width: "100%",
                  padding: "12px 16px",
                  border: "2px solid #f1f5f9",
                  borderRadius: "12px",
                  fontSize: "16px",
                  outline: "none",
                  transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                }}
              />
            </div>

            {/* Descripción */}
            <div>
                          <label
              style={{
                fontSize: "14px",
                color: "#2563eb",
                fontWeight: "600",
                display: "block",
                marginBottom: "6px",
              }}
            >
              Descripción
            </label>
              <textarea
                value={editingNota?.descripcion || ""}
                onChange={(e) =>
                  setEditingNota({ ...editingNota, descripcion: e.target.value })
                }
                placeholder="Descripción detallada (opcional)"
                rows={3}
                style={{
                  width: "100%",
                  padding: "12px 16px",
                  border: "2px solid #f1f5f9",
                  borderRadius: "12px",
                  fontSize: "16px",
                  outline: "none",
                  transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                  resize: "vertical",
                }}
              />
            </div>

                        {/* Proyecto */}
            <div>
              <label
                style={{
                  fontSize: "14px",
                  color: "#2563eb",
                  fontWeight: "600",
                  display: "block",
                  marginBottom: "6px",
                }}
              >
                Proyecto
              </label>
              <CustomDropdown
                value={editingNota?.proyecto_id || ""}
                onChange={(value) =>
                  setEditingNota({ ...editingNota, proyecto_id: value })
                }
                options={[
                  { value: "", label: "Sin proyecto" },
                  ...opcionesProyectos,
                ]}
                placeholder="Seleccionar proyecto"
                icon={Building2}
              />
            </div>

            {/* Prioridad */}
            <div>
                          <label
              style={{
                fontSize: "14px",
                color: "#2563eb",
                fontWeight: "600",
                display: "block",
                marginBottom: "6px",
              }}
            >
              Prioridad
            </label>
              <CustomDropdown
                value={editingNota?.prioridad || "media"}
                onChange={(value) =>
                  setEditingNota({ ...editingNota, prioridad: value })
                }
                options={[
                  { value: "baja", label: "Baja" },
                  { value: "media", label: "Media" },
                  { value: "alta", label: "Alta" },
                ]}
                placeholder="Seleccionar prioridad"
                icon={Star}
              />
            </div>

            {/* Estado */}
            <div>
                          <label
              style={{
                fontSize: "14px",
                color: "#2563eb",
                fontWeight: "600",
                display: "block",
                marginBottom: "6px",
              }}
            >
              Estado
            </label>
              <CustomDropdown
                value={editingNota?.estado || "pendiente"}
                onChange={(value) =>
                  setEditingNota({ ...editingNota, estado: value })
                }
                options={[
                  { value: "pendiente", label: "Pendiente" },
                  { value: "hecho", label: "Hecho" },
                ]}
                placeholder="Seleccionar estado"
                icon={CheckSquare}
              />
            </div>

            {/* Trabajador */}
            <div>
                          <label
              style={{
                fontSize: "14px",
                color: "#2563eb",
                fontWeight: "600",
                display: "block",
                marginBottom: "6px",
              }}
            >
              Trabajador Asignado
            </label>
              <CustomDropdown
                value={editingNota?.trabajador_id || ""}
                onChange={(value) =>
                  setEditingNota({ ...editingNota, trabajador_id: value })
                }
                options={[
                  { value: "", label: "Sin asignar" },
                  ...trabajadores.map((t) => ({
                    value: t.id,
                    label: `${t.nombre} ${t.apellido} (${t.tipo})`,
                  })),
                ]}
                placeholder="Seleccionar trabajador"
                icon={User}
              />
            </div>

            {/* Botones */}
            <div
              style={{
                display: "flex",
                gap: "12px",
                justifyContent: "flex-end",
                marginTop: "20px",
              }}
            >
              <button
                onClick={() => {
                  setShowModal(false);
                  setEditingNota(null);
                }}
                style={{
                  background: "#f1f5f9",
                  color: "#64748b",
                  border: "none",
                  borderRadius: "8px",
                  padding: "12px 20px",
                  fontSize: "16px",
                  fontWeight: "600",
                  cursor: "pointer",
                }}
              >
                Cancelar
              </button>
              <button
                onClick={handleSave}
                disabled={guardando}
                style={{
                  background: "linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)",
                  color: "white",
                  border: "none",
                  borderRadius: "8px",
                  padding: "12px 20px",
                  fontSize: "16px",
                  fontWeight: "600",
                  cursor: guardando ? "not-allowed" : "pointer",
                  opacity: guardando ? 0.6 : 1,
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                {guardando ? (
                  <>
                    <Loader2 size={16} style={{ animation: "spin 1s linear infinite" }} />
                    Guardando...
                  </>
                ) : (
                  <>
                    <Save size={16} />
                    {editingNota?.id ? "Actualizar" : "Crear"}
                  </>
                )}
              </button>
            </div>
          </div>
        </StandardModal>
      )}

      {/* Modal personalizado para alerts */}
      {customModal.isOpen && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
          }}
        >
          <div
            style={{
              background: "white",
              borderRadius: "16px",
              padding: "24px",
              maxWidth: "400px",
              width: "90%",
              boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "12px",
                marginBottom: "16px",
              }}
            >
              {customModal.type === "success" && (
                <CheckCircle2 size={24} color="#10b981" />
              )}
              {customModal.type === "error" && (
                <XCircle size={24} color="#ef4444" />
              )}
              {customModal.type === "confirm" && (
                <AlertCircle size={24} color="#f59e0b" />
              )}
              <h3
                style={{
                  fontSize: "18px",
                  fontWeight: "600",
                  color: "#1f2937",
                  margin: 0,
                }}
              >
                {customModal.title}
              </h3>
            </div>
            
            <p
              style={{
                fontSize: "16px",
                color: "#6b7280",
                margin: "0 0 24px 0",
                lineHeight: "1.5",
              }}
            >
              {customModal.message}
            </p>

            <div
              style={{
                display: "flex",
                gap: "12px",
                justifyContent: "flex-end",
              }}
            >
              {customModal.type === "confirm" && (
                <button
                  onClick={() => setCustomModal({ ...customModal, isOpen: false })}
                  style={{
                    background: "#f3f4f6",
                    color: "#6b7280",
                    border: "none",
                    borderRadius: "8px",
                    padding: "10px 20px",
                    fontSize: "14px",
                    fontWeight: "500",
                    cursor: "pointer",
                  }}
                >
                  Cancelar
                </button>
              )}
              <button
                onClick={() => {
                  if (customModal.onConfirm) {
                    customModal.onConfirm();
                  }
                  setCustomModal({ ...customModal, isOpen: false });
                }}
                style={{
                  background: customModal.type === "error" 
                    ? "#ef4444" 
                    : customModal.type === "confirm"
                    ? "#f59e0b"
                    : "#10b981",
                  color: "white",
                  border: "none",
                  borderRadius: "8px",
                  padding: "10px 20px",
                  fontSize: "14px",
                  fontWeight: "500",
                  cursor: "pointer",
                }}
              >
                {customModal.type === "confirm" ? "Confirmar" : "Aceptar"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default NotasProyecto;
